import { Injectable } from '@angular/core';
import {
  ConfigStatut,
  ConseilConfig,
  ConseilConfigPaginatedCollectionSegment,
  Produit,
} from '@lib/models/generated/graphql';
import { gql } from 'apollo-angular';
import { ApplicationInsightsService, QueryManagerService } from 'finzzle-angular-utils';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  fullAdequationConfigFragment,
  modeDeGestionConfigFragment,
} from 'src/app/components/declare-fiche-conseil/steps/projet-investissement-step/projet-investissement.component';
import { environment } from 'src/environments/environment';
const CONFIG_BY_ID = gql`
  query conseilConfigById($id: String!) {
    conseilConfigPaginated(take: 1, skip: 0, where: { id: { eq: $id } }) {
      items {
        id
        statut
        validityLimitDate
        configFormulaire {
          id
          enveloppeConfig {
            ...FullAdequationConfig
          }
          produitConfigs {
            gestionPossibles {
              ...modeDeGestionConfig
            }
            produit {
              id
            }
            config {
              ...FullAdequationConfig
            }
          }
        }
      }
    }
  }
  ${fullAdequationConfigFragment},
  ${modeDeGestionConfigFragment}
`;

const PRODUIT_BY_ID = gql`
  query produitById($id: Long!) {
    produitById(id: $id) {
      id
      nom
      nomTechniqueProduitBox
      habilitation
      idPartenaireNavigation {
        id
        nom
      }
    }
  }
`;

export type ValidityStatus = 'valid' | 'obsolete' | 'expired';
@Injectable({
  providedIn: 'root',
})
export class ConseilConfigService {
  public currentConseilConfigSubject = new BehaviorSubject<ConseilConfig | null>(null);
  public deprecationWarningSubject = new BehaviorSubject<boolean>(false);
  private validityStatus: ValidityStatus = 'valid';
  private missingAtlasProduitIds = new Set<number>();
  constructor(
    private queryManager: QueryManagerService,
    private snackbarService: OuiSnackbarService,
    private loggerService: ApplicationInsightsService
  ) {}

  onConseilConfigSelected(configfId: string) {
    this.queryManager
      .query<{ conseilConfigPaginated: ConseilConfigPaginatedCollectionSegment }>({
        query: CONFIG_BY_ID,
        variables: {
          id: configfId,
        },
      })
      .subscribe(res => {
        if (res.data?.conseilConfigPaginated?.items && res.data?.conseilConfigPaginated?.items.length > 0) {
          const config = res.data.conseilConfigPaginated.items[0];
          this.enrichConfigProduitsFromAtlas(config).then(enrichedConfig => {
            this.currentConseilConfigSubject.next(enrichedConfig);
          });
          this.validityStatus = this.computeValidityStatus(config);
          this.deprecationWarningSubject.next(this.validityStatus !== 'valid');
        } else {
          this.validityStatus !== 'valid';
          this.currentConseilConfigSubject.next(null);
          this.deprecationWarningSubject.next(false);
        }
      });
  }

  reset() {
    this.validityStatus = 'valid';
    this.currentConseilConfigSubject.next(null);
    this.deprecationWarningSubject.next(false);
  }

  /**
   * Compute the validity status of the given config based on its statut and validityLimitDate
   * @param config The config to check
   * @returns The validity status of the config
   */
  private computeValidityStatus(config: ConseilConfig): ValidityStatus {
    const configStatus = config.statut;
    const configValidityLimitDate = config.validityLimitDate ? new Date(config.validityLimitDate) : undefined;
    const today = new Date();
    today.setHours(0, 0, 0, 0); // set to start of day

    // If the config is active or valid but deprecated, it is valid
    if (configStatus === ConfigStatut.Active || configStatus === ConfigStatut.ValidButDeprecated) {
      return 'valid';
    }

    // If the config is temporary valid but deprecated, and its validity limit date is in the future, it is obsolete
    if (
      configStatus === ConfigStatut.TemporaryValidButDeprecated &&
      configValidityLimitDate &&
      configValidityLimitDate >= today
    ) {
      return 'obsolete';
    }

    // Otherwise, it is expired
    return 'expired';
  }

  getValidityStatus(): ValidityStatus {
    return this.validityStatus;
  }

  async getValidityStatusByConfigId(configId: string): Promise<ValidityStatus> {
    const res = await firstValueFrom(
      this.queryManager.query<{ conseilConfigPaginated: ConseilConfigPaginatedCollectionSegment }>({
        query: CONFIG_BY_ID,
        variables: { id: configId },
        fetchPolicy: 'network-only',
      })
    );

    const config = res.data?.conseilConfigPaginated?.items?.[0];
    if (!config) {
      return 'expired';
    }

    return this.computeValidityStatus(config);
  }

  private async enrichConfigProduitsFromAtlas(config: ConseilConfig): Promise<ConseilConfig> {
    const produitConfigs = config.configFormulaire?.produitConfigs ?? [];
    const enrichedProduitConfigs = await Promise.all(
      produitConfigs.map(async produitConfig => {
        const produitId = produitConfig.produit?.id;
        if (!produitId) return produitConfig;

        const atlasProduit = await this.getAtlasProduitById(produitId, config.id);
        return {
          ...produitConfig,
          produit: {
            ...produitConfig.produit,
            ...atlasProduit,
            id: produitId,
            gestionPossibles: produitConfig.gestionPossibles ?? produitConfig.produit?.gestionPossibles,
            nom: atlasProduit?.nom ?? this.getMissingAtlasProduitLabel(produitId),
          } as Produit,
        };
      })
    );

    if (!config.configFormulaire) {
      return config;
    }

    return {
      ...config,
      configFormulaire: {
        ...config.configFormulaire,
        produitConfigs: enrichedProduitConfigs,
      },
    };
  }

  private async getAtlasProduitById(id: number, configId: string): Promise<Produit | null> {
    try {
      const result = await firstValueFrom(
        this.queryManager.query<{ produitById: Produit | null }>(
          {
            query: PRODUIT_BY_ID,
            variables: { id },
            fetchPolicy: 'network-only',
            errorPolicy: 'all',
          },
          true
        )
      );

      const produit = result?.data?.produitById ?? null;
      if (!produit) {
        this.notifyMissingAtlasProduit(id, configId);
      }

      return produit;
    } catch {
      this.notifyMissingAtlasProduit(id, configId);
      return null;
    }
  }

  private getMissingAtlasProduitLabel(id: number): string {
    return environment.production
      ? `Produit ${id} introuvable dans Atlas - contactez le support`
      : `Produit ${id} absent d'Atlas (possible hors prod)`;
  }

  private notifyMissingAtlasProduit(produitId: number, configId: string): void {
    if (this.missingAtlasProduitIds.has(produitId)) return;

    this.missingAtlasProduitIds.add(produitId);
    const missingProduitIds = Array.from(this.missingAtlasProduitIds).join(', ');

    if (environment.production) {
      this.loggerService.logEvent('MissingAtlasProduitInConseilConfig', {
        produitId,
        configId,
      });
      this.snackbarService.open(
        `Produits introuvables dans Atlas : ${missingProduitIds}. Merci de contacter le support.`,
        'error',
        7000
      );
    } else {
      this.snackbarService.open(
        `Produits absents d'Atlas : ${missingProduitIds}. En hors prod, cela peut arriver.`,
        'warning',
        7000
      );
    }
  }
}
